import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthBootstrap } from '@/hooks/useAuthBootstrap';
import { ProtectedRoute, RequirePermission, RequirePlatformAdmin } from '@/routes/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { PERMISSIONS } from '@/lib/permissions';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const LeadsListPage = lazy(() => import('@/pages/leads/LeadsListPage'));
const LeadDetailPage = lazy(() => import('@/pages/leads/LeadDetailPage'));
const CompaniesListPage = lazy(() => import('@/pages/companies/CompaniesListPage'));
const CompanyDetailPage = lazy(() => import('@/pages/companies/CompanyDetailPage'));
const ContactsListPage = lazy(() => import('@/pages/contacts/ContactsListPage'));
const ContactDetailPage = lazy(() => import('@/pages/contacts/ContactDetailPage'));
const DuplicatesPage = lazy(() => import('@/pages/duplicates/DuplicatesPage'));
const CasesListPage = lazy(() => import('@/pages/cases/CasesListPage'));
const CaseDetailPage = lazy(() => import('@/pages/cases/CaseDetailPage'));
const KnowledgeBaseListPage = lazy(() => import('@/pages/knowledgeBase/KnowledgeBaseListPage'));
const KnowledgeArticleDetailPage = lazy(() => import('@/pages/knowledgeBase/KnowledgeArticleDetailPage'));
const SequencesListPage = lazy(() => import('@/pages/sequences/SequencesListPage'));
const SequenceDetailPage = lazy(() => import('@/pages/sequences/SequenceDetailPage'));
const CampaignsListPage = lazy(() => import('@/pages/campaigns/CampaignsListPage'));
const CampaignDetailPage = lazy(() => import('@/pages/campaigns/CampaignDetailPage'));
const MeetingsPage = lazy(() => import('@/pages/MeetingsPage'));
const TasksPage = lazy(() => import('@/pages/TasksPage'));
const DocumentsPage = lazy(() => import('@/pages/DocumentsPage'));
const ActivitiesPage = lazy(() => import('@/pages/ActivitiesPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const CustomReportsPage = lazy(() => import('@/pages/reports/CustomReportsPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const RolesPage = lazy(() => import('@/pages/roles/RolesPage'));
const CustomizationPage = lazy(() => import('@/pages/customization/CustomizationPage'));
const CustomObjectDetailPage = lazy(() => import('@/pages/customObjects/CustomObjectDetailPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const AuditLogsPage = lazy(() => import('@/pages/AuditLogsPage'));
// Phase 13 (super admin), slice 1 / slice 3.
const PlatformOrganizationsPage = lazy(() => import('@/pages/platformAdmin/PlatformOrganizationsPage'));
const PlatformMetricsPage = lazy(() => import('@/pages/platformAdmin/PlatformMetricsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function PageFallback() {
  return (
    <div className="flex h-64 w-full items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

export default function App() {
  useAuthBootstrap();

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />

        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          <Route path="/leads" element={<LeadsListPage />} />
          <Route path="/leads/:id" element={<LeadDetailPage />} />

          <Route path="/companies" element={<CompaniesListPage />} />
          <Route path="/companies/:id" element={<CompanyDetailPage />} />

          <Route path="/contacts" element={<ContactsListPage />} />
          <Route path="/contacts/:id" element={<ContactDetailPage />} />

          <Route path="/duplicates" element={<DuplicatesPage />} />

          <Route path="/cases" element={<CasesListPage />} />
          <Route path="/cases/:id" element={<CaseDetailPage />} />

          <Route path="/knowledge-base" element={<KnowledgeBaseListPage />} />
          <Route path="/knowledge-base/:id" element={<KnowledgeArticleDetailPage />} />

          <Route
            path="/sequences"
            element={
              // Phase 9 ("advanced CRM" slice) — sequences, Stage 2. Gated at the route level
              // (unlike /cases and /knowledge-base) since SEQUENCES_MANAGE covers the whole
              // module including viewing — see that permission's own comment in lib/permissions.ts.
              <RequirePermission permission={PERMISSIONS.SEQUENCES_MANAGE}>
                <SequencesListPage />
              </RequirePermission>
            }
          />
          <Route
            path="/sequences/:id"
            element={
              <RequirePermission permission={PERMISSIONS.SEQUENCES_MANAGE}>
                <SequenceDetailPage />
              </RequirePermission>
            }
          />

          <Route path="/campaigns" element={<CampaignsListPage />} />
          <Route path="/campaigns/:id" element={<CampaignDetailPage />} />

          <Route path="/meetings" element={<MeetingsPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/activities" element={<ActivitiesPage />} />

          <Route
            path="/reports"
            element={
              <RequirePermission permission={PERMISSIONS.REPORTS_VIEW}>
                <ReportsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/custom-reports"
            element={
              // Phase 10 (reporting/dashboard builder), slice 1 — same base permission as /reports
              // itself; ownership/shared-management checks for individual reports live on the page.
              // Deliberately NOT nested under /reports/* — Sidebar.tsx's isPathActive matches any
              // path one segment below a nav item's `to` as also "active" for that item (it's built
              // for detail pages like /sequences/:id), so /reports/custom would incorrectly also
              // highlight the "Reports" nav item as active.
              <RequirePermission permission={PERMISSIONS.REPORTS_VIEW}>
                <CustomReportsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/users"
            element={
              // Phase 3: was RequireRole roles={['ADMIN']} — USERS_MANAGE is granted to ADMIN by
              // default, so this is a zero-behavior-change swap to the permission this route was
              // always really gated by (see backend/src/modules/users/users.routes.ts).
              <RequirePermission permission={PERMISSIONS.USERS_MANAGE}>
                <UsersPage />
              </RequirePermission>
            }
          />
          <Route
            path="/roles"
            element={
              <RequirePermission permission={PERMISSIONS.ROLES_VIEW}>
                <RolesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/customization"
            element={
              // Phase 4: gated on CUSTOM_FIELDS_MANAGE — see CustomizationPage.tsx's own comment
              // for why one permission is enough at the route level even though the page covers
              // two separate permissions.
              <RequirePermission permission={PERMISSIONS.CUSTOM_FIELDS_MANAGE}>
                <CustomizationPage />
              </RequirePermission>
            }
          />
          <Route
            path="/objects/:definitionId"
            element={
              // Phase 5: gated on CUSTOM_OBJECTS_MANAGE — same "one permission covers the whole
              // page" simplification as /customization's own route guard.
              <RequirePermission permission={PERMISSIONS.CUSTOM_OBJECTS_MANAGE}>
                <CustomObjectDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <RequirePermission permission={PERMISSIONS.AUDIT_LOGS_VIEW}>
                <AuditLogsPage />
              </RequirePermission>
            }
          />
          <Route path="/settings" element={<SettingsPage />} />

          <Route
            path="/platform-admin/organizations"
            element={
              // Phase 13 (super admin), slice 1 — see RequirePlatformAdmin's own comment for why
              // this is gated on the raw isPlatformAdmin flag rather than a RequirePermission call.
              <RequirePlatformAdmin>
                <PlatformOrganizationsPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform-admin/metrics"
            element={
              // Phase 13 (super admin), slice 3 — same guard as the organizations console above.
              <RequirePlatformAdmin>
                <PlatformMetricsPage />
              </RequirePlatformAdmin>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
